/**
 * Risk-Based CAPTCHA System - Type Definitions
 *
 * Comprehensive TypeScript interfaces for the hCaptcha integration.
 * Supports invisible and visible CAPTCHA modes based on backend risk assessment.
 *
 * @author AmalQ Frontend Team
 * @date 30-12-2025
 */

/**
 * CAPTCHA modes returned by backend risk assessment
 * - invisible: Background verification (user doesn't see anything)
 * - visible: User must solve a puzzle
 */
export type CaptchaMode = 'invisible' | 'visible';

/**
 * Current state of CAPTCHA processing
 */
export type CaptchaState =
  | 'idle' // No CAPTCHA needed yet
  | 'loading' // Loading hCaptcha script
  | 'verifying' // Invisible challenge executing (shows subtle indicator)
  | 'challenged' // Visible challenge displayed
  | 'completed' // Token obtained successfully
  | 'error'; // CAPTCHA failed

/**
 * Error codes for CAPTCHA failures
 */
export type CaptchaErrorCode =
  | 'LOAD_FAILED' // Script failed to load
  | 'EXECUTION_FAILED' // Challenge execution failed
  | 'NETWORK_ERROR' // Network issue during verification
  | 'TIMEOUT' // Challenge timed out
  | 'TOKEN_EXPIRED' // Token expired before use
  | 'CHALLENGE_CLOSED' // User closed visible challenge
  | 'INVALID_SITE_KEY' // Site key invalid
  | 'RATE_LIMITED' // Too many attempts
  | 'UNKNOWN'; // Unknown error

/**
 * CAPTCHA error structure
 */
export interface CaptchaError {
  /** Error classification */
  code: CaptchaErrorCode;
  /** Human-readable error message */
  message: string;
  /** Whether the error is recoverable (user can retry) */
  recoverable: boolean;
}

/**
 * Backend CAPTCHA challenge response (HTTP 202 or 400)
 * Returned when backend determines CAPTCHA verification is needed
 */
export interface CaptchaChallenge {
  /** Always true when CAPTCHA is required */
  captchaRequired: true;
  /** Type of challenge to show */
  captchaMode: CaptchaMode;
  /** hCaptcha site key for widget */
  siteKey: string;
  /** Unique request ID for tracking */
  riskRequestId: string;
  /** Optional message from backend */
  message?: string;
}

/**
 * Result of CAPTCHA execution
 */
export interface CaptchaResult {
  /** Whether CAPTCHA was successfully completed */
  success: boolean;
  /** hCaptcha token to send with retry request */
  token?: string;
  /** Error details if failed */
  error?: CaptchaError;
}

/**
 * CAPTCHA Context value exposed to consumers
 */
export interface CaptchaContextValue {
  /** Current CAPTCHA state */
  state: CaptchaState;
  /** Current error (if state is 'error') */
  error: CaptchaError | null;
  /**
   * Trigger a CAPTCHA challenge
   * Returns Promise that resolves with token when completed
   * Rejects with CaptchaError if failed/cancelled
   */
  showChallenge: (challenge: CaptchaChallenge) => Promise<string>;
  /** Reset CAPTCHA state to idle */
  reset: () => void;
}

/**
 * Props for CaptchaProvider component
 */
export interface CaptchaProviderProps {
  children: React.ReactNode;
}

/**
 * Props for CaptchaWidget component (visible CAPTCHA modal)
 */
export interface CaptchaWidgetProps {
  /** hCaptcha site key */
  siteKey: string;
  /** Called when user successfully completes challenge */
  onVerify: (token: string) => void;
  /** Called when challenge encounters an error */
  onError: (error: CaptchaError) => void;
  /** Called when token expires */
  onExpire: () => void;
  /** Called when user closes the modal */
  onClose: () => void;
  /** Theme for hCaptcha widget */
  theme?: 'light' | 'dark';
  /** Size of hCaptcha widget */
  size?: 'normal' | 'compact';
  /** Language code for hCaptcha */
  language?: string;
}

/**
 * Props for VerifyingIndicator component (subtle loading indicator)
 */
export interface VerifyingIndicatorProps {
  /** Whether to show the indicator */
  visible?: boolean;
  /** Custom message (default: "Verifying...") */
  message?: string;
}

/**
 * API response type extended with CAPTCHA challenge fields
 * Use this for responses that may require CAPTCHA
 */
export interface ApiResponseWithCaptcha<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  timestamp?: string;
  // CAPTCHA-specific fields (present on 202/400 when CAPTCHA needed)
  captchaRequired?: boolean;
  captchaMode?: CaptchaMode;
  siteKey?: string;
  riskRequestId?: string;
}

/**
 * Request body extension for CAPTCHA retry
 * Add these fields when retrying after CAPTCHA challenge
 */
export interface CaptchaRetryFields {
  /** hCaptcha token from completed challenge */
  hcaptchaToken?: string;
  /** Risk request ID from challenge response (for tracking) */
  riskRequestId?: string;
}

/**
 * hCaptcha render options
 */
export interface HCaptchaRenderOptions {
  sitekey: string;
  size?: 'normal' | 'compact' | 'invisible';
  theme?: 'light' | 'dark';
  hl?: string;
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: (error?: unknown) => void;
  'close-callback'?: () => void;
}

/**
 * hCaptcha global object type
 */
export interface HCaptchaGlobal {
  render: (container: string | HTMLElement, options: HCaptchaRenderOptions) => string;
  execute: (widgetId?: string, options?: { async: boolean }) => Promise<{ response: string }>;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
  getResponse: (widgetId?: string) => string;
}

/**
 * Extend Window interface for hCaptcha
 */
declare global {
  interface Window {
    hcaptcha?: HCaptchaGlobal;
    hcaptchaOnLoad?: () => void;
  }
}

/**
 * Type guard to check if response requires CAPTCHA
 */
export function isCaptchaChallenge(response: unknown): response is CaptchaChallenge {
  if (typeof response !== 'object' || response === null) {
    return false;
  }

  const obj = response as Record<string, unknown>;

  return (
    obj.captchaRequired === true &&
    typeof obj.siteKey === 'string' &&
    typeof obj.riskRequestId === 'string' &&
    (obj.captchaMode === 'invisible' || obj.captchaMode === 'visible')
  );
}

/**
 * Type guard to check if API response contains CAPTCHA challenge
 */
export function hasCaptchaChallenge<T>(
  response: ApiResponseWithCaptcha<T>
): response is ApiResponseWithCaptcha<T> & CaptchaChallenge {
  return (
    response.captchaRequired === true &&
    typeof response.siteKey === 'string' &&
    typeof response.riskRequestId === 'string' &&
    (response.captchaMode === 'invisible' || response.captchaMode === 'visible')
  );
}

/**
 * Extract CaptchaChallenge from API response
 */
export function extractCaptchaChallenge<T>(
  response: ApiResponseWithCaptcha<T>
): CaptchaChallenge | null {
  if (!hasCaptchaChallenge(response)) {
    return null;
  }

  return {
    captchaRequired: true,
    captchaMode: response.captchaMode,
    siteKey: response.siteKey,
    riskRequestId: response.riskRequestId,
    message: response.message,
  };
}
